import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sparkles, Crown, Check, ChevronRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function Welcome() {
    const { user, loading, refresh } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/login", { replace: true });
            return;
        }
        api.get("/plans")
            .then((r) => setPlans(r.data))
            .catch((err) => console.warn("plans fetch failed:", err?.message || err));
    }, [user, loading, navigate]);

    // If somehow they land here already-active, send them home (only after data is fresh)
    useEffect(() => {
        if (!loading && user && user.subscription_status === "active") {
            navigate("/portal", { replace: true });
        }
    }, [loading, user, navigate]);

    const claim = async (planId) => {
        setBusy(planId);
        try {
            const { data } = await api.post("/checkout/session", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            if (data?.url) {
                window.location.href = data.url;
            } else {
                toast.error("Checkout failed");
                setBusy(null);
            }
        } catch {
            toast.error("Checkout failed");
            setBusy(null);
        }
    };

    const skip = async () => {
        await refresh();
        navigate("/portal", { replace: true });
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-rose/30 border-t-rose rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ink text-white relative overflow-hidden bg-grain">
            {/* dismiss */}
            <button
                onClick={skip}
                data-testid="welcome-skip"
                className="absolute top-6 right-6 z-20 p-2 rounded-full text-white/50 hover:text-white hover:bg-white/5 transition"
                aria-label="Skip"
            >
                <X className="w-5 h-5" />
            </button>

            {/* glow blobs */}
            <div className="pointer-events-none absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-rose/20 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-gold/15 blur-[120px]" />

            <main className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-12 py-20">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 bg-gold/10 text-gold">
                        <Sparkles className="w-4 h-4" />
                        <span className="text-xs tracking-[0.3em] uppercase font-medium">Welcome bonus · One-time only</span>
                    </div>
                    <h1 className="mt-8 font-serif text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
                        Hey {user.name?.split(" ")[0] || "gorgeous"} —
                        <br />
                        <span className="italic text-rose">your first month</span>
                        <br />
                        is <span className="gold-underline">half off.</span>
                    </h1>
                    <p className="mt-8 text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
                        As a thank-you for joining, your first 30 days are <span className="text-gold font-medium">50% off</span>.
                        Pick the plan that fits — your bonus auto-applies at checkout.
                    </p>
                </div>

                <div className="mt-16 grid md:grid-cols-2 gap-6">
                    {plans.map((p) => {
                        const isAcrylic = p.id === "acrylic";
                        const half = (p.price / 2).toFixed(2);
                        return (
                            <div
                                key={p.id}
                                data-testid={`welcome-plan-${p.id}`}
                                className={`relative rounded-3xl p-8 ${
                                    isAcrylic ? "tracing-beam" : "bg-surface border border-white/10"
                                }`}
                            >
                                {isAcrylic && (
                                    <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-gold text-ink text-xs tracking-widest uppercase">
                                        Most Popular
                                    </span>
                                )}
                                <div className="flex items-center gap-2">
                                    {isAcrylic ? <Crown className="w-5 h-5 text-gold" /> : <Sparkles className="w-5 h-5 text-rose" />}
                                    <p className="text-xs tracking-[0.3em] uppercase text-white/60">{p.name}</p>
                                </div>
                                <div className="mt-6 flex items-baseline gap-3">
                                    <span className="font-serif text-5xl text-white/35 line-through">${p.price}</span>
                                    <span className="font-serif text-7xl text-rose">${half}</span>
                                    <span className="text-white/50 mb-3 text-sm">first month</span>
                                </div>
                                <p className="text-xs text-white/50 mt-1">Then ${p.price}/month. Cancel anytime.</p>
                                <ul className="mt-6 space-y-2 text-sm text-white/75">
                                    {(isAcrylic
                                        ? [
                                              "1 full acrylic set + unlimited fill-ins",
                                              "AI 3D nail art (3,400 DPI)",
                                              "Same-day · book before 6pm",
                                              "Emergency in under an hour (small upcharge)",
                                              "Mobile — Glam on Wheels",
                                          ]
                                        : [
                                              "Unlimited patent-pending gel sets",
                                              "Lasts 7–14 days · UV-cured",
                                              "AI 3D nail art (3,400 DPI)",
                                              "Same-day · book before 6pm",
                                              "Emergency in under an hour (small upcharge)",
                                          ]
                                    ).map((f) => (
                                        <li key={f} className="flex gap-2">
                                            <Check className="w-4 h-4 text-rose mt-0.5" /> <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    data-testid={`welcome-claim-${p.id}`}
                                    onClick={() => claim(p.id)}
                                    disabled={busy === p.id}
                                    className={`mt-8 w-full px-6 py-3.5 rounded-full transition font-medium flex items-center justify-center gap-2 ${
                                        isAcrylic
                                            ? "bg-rose text-ink hover:bg-roseHover"
                                            : "border border-rose text-rose hover:bg-rose hover:text-ink"
                                    } disabled:opacity-50`}
                                >
                                    {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    Claim — ${half} <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-12 text-center">
                    <button
                        onClick={skip}
                        data-testid="welcome-later"
                        className="text-white/50 hover:text-white transition text-sm tracking-wide"
                    >
                        I'll decide later — take me to my portal
                    </button>
                    <p className="mt-3 text-xs text-white/30">Bonus stays on your account until you redeem it on your first payment.</p>
                </div>

                <div className="mt-10 text-center">
                    <Link to="/" className="text-xs text-white/30 hover:text-white/60 tracking-widest uppercase">
                        24K Glow On The Go · Glam on Wheels
                    </Link>
                </div>
            </main>
        </div>
    );
}
